import React from 'react';
import {useNavigate} from 'react-router';

import {favoriteContent} from 'common/report/queries/app-api/content-client';
import {useTranslation} from 'common/i18n/hooks';
import {Content} from 'common/types/app-api/content';

import {TrackingEvent, TrackingSource} from '../tracking';
import {getContentTypeName, performContentAction} from '../helpers';

export type UseFavoriteContentParams = {
  projectId: string | number;
  workspaceId?: string | number;
  source?: TrackingSource;
};

export function useFavoriteContent({projectId, workspaceId, source}: UseFavoriteContentParams) {
  const i18n = useTranslation();
  const navigate = useNavigate();

  return React.useCallback(
    (content: Content, onSuccess: (result: Content) => void) => {
      const isFavorited = !content.is_favorited;
      const entityTypeName = getContentTypeName(content, i18n);
      return performContentAction({
        content,
        action: async () => {
          await favoriteContent({contentId: content.id, projectId, isFavorited});
          return {
            ...content,
            is_favorited: isFavorited,
          };
        },
        onSuccess: () => {
          onSuccess({
            ...content,
            is_favorited: isFavorited,
          });
        },
        successMessage: isFavorited
          ? i18n.t(`sidenav.favorite-content-success`, {entityTypeName})
          : i18n.t(`sidenav.unfavorite-content-success`, {entityTypeName}),
        errorMessage: isFavorited
          ? i18n.t(`sidenav.favorite-content-error`, {entityTypeName})
          : i18n.t(`sidenav.unfavorite-content-error`, {entityTypeName}),
        successTrackingEvent: isFavorited ? TrackingEvent.Favorited : TrackingEvent.Unfavorited,
        errorTrackingEvent: isFavorited ? TrackingEvent.Favorited : TrackingEvent.Unfavorited,
        source,
      });
    },
    [i18n, navigate, projectId, workspaceId, source],
  );
}
